import { useTranslation } from 'react-i18next';
import {
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';
import type { WasteMetrics } from '../../../api/queries/analytics.queries';

const COLORS = ['#EF4444', '#F59E0B', '#8B5CF6', '#06B6D4', '#10B981', '#F97316', '#EC4899'];

export function WasteTrendChart({ data }: { data?: WasteMetrics['trend'] }) {
  const { t, i18n } = useTranslation();

  if (!data || data.length === 0) {
    return <EmptyChart message={t('analytics.noDataAvailable')} />;
  }

  const chartData = data.map((item) => ({
    date: new Date(item.date).toLocaleDateString(i18n.language === 'th' ? 'th-TH' : 'en-US', { month: 'short', day: 'numeric' }),
    value: item.value,
    count: item.count,
  }));

  return (
    <ResponsiveContainer width="100%" height={300}>
      <AreaChart data={chartData}>
        <defs>
          <linearGradient id="colorWaste" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#EF4444" stopOpacity={0.8}/>
            <stop offset="95%" stopColor="#EF4444" stopOpacity={0}/>
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis dataKey="date" fontSize={12} />
        <YAxis fontSize={12} />
        <Tooltip
          formatter={(value, name) => [
            name === 'value' ? `฿${Number(value).toLocaleString()}` : value,
            name === 'value' ? t('analytics.wasteValue') : t('analytics.entries'),
          ]}
        />
        <Legend />
        <Area
          type="monotone"
          dataKey="value"
          stroke="#EF4444"
          fillOpacity={1}
          fill="url(#colorWaste)"
          name={t('analytics.wasteValue')}
        />
        <Line
          type="monotone"
          dataKey="count"
          stroke="#F59E0B"
          name={t('analytics.entries')}
          strokeWidth={2}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export function WasteByCategoryChart({ data }: { data?: WasteMetrics['byCategory'] }) {
  const { t } = useTranslation();

  if (!data || data.length === 0) {
    return <EmptyChart message={t('analytics.noDataAvailable')} />;
  }

  const chartData = data.map((item) => ({
    name: t(`waste.${item.category}`) || item.category,
    value: item.value,
    count: item.count,
  }));

  return (
    <ResponsiveContainer width="100%" height={250}>
      <PieChart>
        <Pie
          data={chartData}
          cx="50%"
          cy="50%"
          innerRadius={50}
          outerRadius={80}
          fill="#8884d8"
          paddingAngle={2}
          dataKey="value"
          label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
        >
          {chartData.map((_, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip
          formatter={(value) => [`฿${Number(value).toLocaleString()}`, t('analytics.wasteValue')]}
        />
      </PieChart>
    </ResponsiveContainer>
  );
}

export function WasteByIngredientChart({ data }: { data?: WasteMetrics['byIngredient'] }) {
  const { t } = useTranslation();

  if (!data || data.length === 0) {
    return <EmptyChart message={t('analytics.noDataAvailable')} />;
  }

  const chartData = data.slice(0, 8).map((item) => ({
    name: item.ingredientName.length > 12
      ? item.ingredientName.substring(0, 12) + '...'
      : item.ingredientName,
    fullName: item.ingredientName,
    value: item.value,
    quantity: item.quantity,
    unit: item.unit,
  }));

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis dataKey="name" fontSize={10} angle={-45} textAnchor="end" height={60} />
        <YAxis fontSize={12} />
        <Tooltip
          formatter={(value) => [`฿${Number(value).toLocaleString()}`, t('analytics.wasteValue')]}
          labelFormatter={(_, payload) => payload?.[0]?.payload?.fullName || ''}
        />
        <Bar dataKey="value" fill="#EF4444" name={t('analytics.wasteValue')} radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function EmptyChart({ message }: { message: string }) {
  return (
    <div className="h-[250px] flex items-center justify-center bg-secondary/20 rounded">
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  );
}
